import { t } from '@lingui/macro';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useEffect, useState } from 'react';
import { AlertDialog } from 'components/dialogs/AlertDialog';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { AssignTodoModal_assignTodo_Mutation } from './__generated__/AssignTodoModal_assignTodo_Mutation.graphql';
import { Member } from 'types/membership';
import { AssignTodoModal_todo$key } from './__generated__/AssignTodoModal_todo.graphql';
import { SpaceMemberSelector } from 'apps/TenantApp/components/MemberSelector/SpaceMemberSelector';

const assignTodoMutation = graphql`
  mutation AssignTodoModal_assignTodo_Mutation($id: ID!, $memberId: ID) {
    assignTodo(input: { id: $id, memberId: $memberId }) {
      todoUpdated {
        ...AssignTodoModal_todo
        ...TodoAssignee_todo
      }
    }
  }
`;

const fragment = graphql`
  fragment AssignTodoModal_todo on Todo {
    id
    documentNode {
      space {
        id
      }
    }
    assignee {
      id
      name
      ...UserHandle_member
      ...UserAvatar_member
    }
  }
`;

interface AssignTodoModalProps extends OverlayProps {
  todo: AssignTodoModal_todo$key;
}

export const AssignTodoModal = ({
  todo,
  ...overlayProps
}: AssignTodoModalProps) => {
  const { id, documentNode, assignee } = useFragment(fragment, todo);
  const [assignTodo, isLoading] =
    useMutation<AssignTodoModal_assignTodo_Mutation>(assignTodoMutation);
  const { isOpen, onClose } = overlayProps;

  function handleActionClick(memberId: string) {
    memberId
      ? assignTodo({ variables: { id, memberId }, onCompleted: onClose })
      : assignTodo({ variables: { id }, onCompleted: onClose });
    onClose();
  }

  const initialSelectedMemberId = assignee?.id || '';

  const [selectedMemberId, setSelectedMemberId] = useState<string>(
    initialSelectedMemberId,
  );

  const isDirty = initialSelectedMemberId !== selectedMemberId;

  useEffect(() => {
    setSelectedMemberId(initialSelectedMemberId);
  }, [isOpen]);

  return (
    <AlertDialog
      {...overlayProps}
      title={t`Assignee`}
      cancelButton={{ title: t`Cancel`, onClick: onClose }}
      actionButton={{
        title: t`Save`,
        onClick: () => handleActionClick(selectedMemberId),
        isDisabled: !isDirty,
        isLoading,
      }}
    >
      {isOpen && (
        <SpaceMemberSelector
          spaceId={documentNode.space.id}
          selectedMemberIds={selectedMemberId ? [selectedMemberId] : []}
          disableEmailInvite
          selectedEmails={[]}
          onSelectedMembersUpdate={(memberIds) =>
            setSelectedMemberId(memberIds.length >= 1 ? memberIds[0] : '')
          }
          onSelectedEmailsUpdate={() => {}}
          multiple={false}
        />
      )}
    </AlertDialog>
  );
};

interface UnsuspendedMemberSelector {
  spaceId: string;
  selectedMemberId?: string;
  currentAssignee?: Member;
  onSelectMember(memberId: string): void;
}

const UnsuspendedMemberSelector = ({
  spaceId,
  selectedMemberId,
  onSelectMember,
}: UnsuspendedMemberSelector) => {
  return (
    <SpaceMemberSelector
      spaceId={spaceId}
      selectedMemberIds={selectedMemberId ? [selectedMemberId] : []}
      disableEmailInvite
      selectedEmails={[]}
      onSelectedMembersUpdate={(memberIds) =>
        onSelectMember(memberIds.length >= 1 ? memberIds[0] : '')
      }
      onSelectedEmailsUpdate={() => {}}
      multiple={false}
    />
  );
};
